import {
    Box,
    CircularProgress,
    InputAdornment,
    ThemeProvider,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { Form, Formik, useFormikContext } from 'formik';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import FormField from 'components/common/formikForm/formField';
import FormGridLayout from 'components/common/formikForm/formGridLayout';
import SubmitButton from 'components/common/formikForm/SubmitButton';
import PageBackground from 'components/v2/PageBackground';

import api from 'api';
import useCheckEmailAvailability, { useDebouncedValue } from 'hooks/useCheckEmail';
import useTranslate from 'hooks/useTranslate';
import { setAlert } from 'redux/alert';
import appThemeV2 from 'theme/themeV2';
import type { SalesOnboardingFormInterface } from 'types';
import {
    salesOnboardingInitialValues,
    salesOnboardingFormValidationSchema,
} from 'utils/validations/onboarding';

export default function SalesOnboarding() {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const theme = useTheme();
    const { translate } = useTranslate();

    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [submitting, setSubmitting] = useState(false);
    /** Normalized email last confirmed as already in use (sync Yup test — no API). */
    const [takenEmail, setTakenEmail] = useState<string | null>(null);

    const validationSchema = useMemo(
        () => salesOnboardingFormValidationSchema(translate, takenEmail),
        [translate, takenEmail]
    );

    const handleSubmit = async (values: SalesOnboardingFormInterface) => {
        try {
            setSubmitting(true);
            const payload = {
                email: values.email,
                firstName: values.firstName,
                lastName: values.lastName,
                permissions: [
                    values.selfAssessment && 'EDIT PROFILE',
                    values.showResults && 'VIEW ASSESSMENT RESULTS',
                ].filter(Boolean),
            };

            const request = await api.externalClient.post(payload);
            if (request.status === 200 || request.status === 201) {
                dispatch(
                    setAlert({
                        message: translate('clientSuccessfullyAdded'),
                        type: 'success',
                    })
                );
                if (values.selfAssessment) {
                    navigate(`/dashboard/${request.data?.id}/client-profile/agreement`);
                } else {
                    navigate(`assessment/${request.data?.id}`);
                }
            }
        } catch {
            dispatch(setAlert({ message: translate('somethingWentWrong'), type: 'error' }));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <ThemeProvider theme={appThemeV2}>
            <PageBackground component="Paper" rootSx={{ minHeight: 'auto' }}>
                <Formik
                    initialValues={salesOnboardingInitialValues}
                    validationSchema={validationSchema}
                    onSubmit={handleSubmit}
                >
                    <Form>
                        <SalesOnboardingFormFields
                            submitting={submitting}
                            isMobile={isMobile}
                            takenEmail={takenEmail}
                            onTakenEmailChange={setTakenEmail}
                        />
                    </Form>
                </Formik>
            </PageBackground>
        </ThemeProvider>
    );
}

/** Email API runs only when the (debounced) email value changes — not on other fields. */
function SalesOnboardingFormFields({
    submitting,
    isMobile,
    takenEmail,
    onTakenEmailChange,
}: {
    submitting: boolean;
    isMobile: boolean;
    takenEmail: string | null;
    onTakenEmailChange: (email: string | null) => void;
}) {
    const { values, setFieldError, setFieldTouched } =
        useFormikContext<SalesOnboardingFormInterface>();
    const { translate } = useTranslate();
    const checkEmail = useCheckEmailAvailability();
    const debouncedEmail = useDebouncedValue(values.email, 400);
    const [emailChecking, setEmailChecking] = useState(false);

    // Keep latest helpers/callbacks out of the effect deps so setFieldError /
    // setTakenEmail cannot re-trigger the effect and leave the loader stuck.
    const latestRef = useRef({
        checkEmail,
        onTakenEmailChange,
        setFieldError,
        setFieldTouched,
        translate,
        takenEmail,
    });
    latestRef.current = {
        checkEmail,
        onTakenEmailChange,
        setFieldError,
        setFieldTouched,
        translate,
        takenEmail,
    };

    useEffect(() => {
        const email = debouncedEmail?.trim().toLowerCase();
        if (!email || !email.includes('@')) {
            latestRef.current.onTakenEmailChange(null);
            setEmailChecking(false);
            return;
        }

        let active = true;
        setEmailChecking(true);

        void latestRef.current
            .checkEmail(email)
            .then((available) => {
                if (!active) return;

                const nextTaken = available ? null : email;
                if (latestRef.current.takenEmail !== nextTaken) {
                    latestRef.current.onTakenEmailChange(nextTaken);
                }

                if (!available) {
                    latestRef.current.setFieldTouched('email', true, false);
                    latestRef.current.setFieldError(
                        'email',
                        latestRef.current.translate('emailAlreadyInUse')
                    );
                }
            })
            .finally(() => {
                // Always clear loader for this run when it is still the active one.
                if (active) setEmailChecking(false);
            });

        return () => {
            active = false;
        };
    }, [debouncedEmail]);

    return (
        <>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                {translate('clientDetails')}
            </Typography>
            <FormGridLayout>
                <FormField
                    name="firstName"
                    label="clientsFirstName"
                    required
                    disabled={submitting}
                />
                <FormField name="lastName" label="clientsLastName" required disabled={submitting} />
                <FormField
                    name="email"
                    label="clientsEmail"
                    type="email"
                    required
                    disabled={submitting}
                    slotProps={{
                        input: {
                            endAdornment: emailChecking ? (
                                <InputAdornment position="end">
                                    <CircularProgress color="primary" size={18} />
                                </InputAdornment>
                            ) : undefined,
                        },
                    }}
                />
            </FormGridLayout>

            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                {translate('permissions')}
            </Typography>
            <FormGridLayout>
                <FormField
                    type="checkbox"
                    name="selfAssessment"
                    label="selfAssessment"
                    disabled={submitting}
                />
                <FormField
                    type="checkbox"
                    name="showResults"
                    label="showResults"
                    disabled={submitting}
                />
            </FormGridLayout>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <SubmitButton loading={submitting} fullWidth={isMobile}>
                    save&Proceed
                </SubmitButton>
            </Box>
        </>
    );
}
